import React, { useState } from "react";
import {
  Grid,
  FormControlLabel,
  FormHelperText,
  Switch,
  Divider,
  Autocomplete,
  FormControl,
  Checkbox,
  TextField,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import { Close, Print } from "@mui/icons-material";

import Button from "components/common/button/Button";
import FormTextField from "components/FormFields/FormTextField";
import FormSelectField from "components/FormFields/FormSelect";
import {
  LETTER_TEMPLATE_DEVICE_TYPES,
  TRANSMISSION_CYCLE_OPTIONS,
} from "./letterTemplateTypes";
import useLetterTemplateForm from "./hooks/useLetterTemplateForm";
import LetterTemplatePreview from "./LetterTemplatePreview";

const LetterTemplateForm = ({
  handleClose,
  selectedTemplate = null,
  isUpdate = false,
  active = true,
}) => {
  const {
    values,
    hubItems,
    isSubmitting,
    isHtmlEditor,
    submitClicked,
    accountOptions,
    deviceModelOptions,
    manufacturerList,
    availableLanguages,
    selectedLanguageTab,
    isActiveContentMissing,

    filter,
    handleSubmit,
    handleCancel,
    setFieldValue,
    setIsHtmlEditor,
    setActiveContent,
    getActiveContent,
    handlePrintPreview,
    handleAddTranslation,
    setSelectedLanguageTab,
    handleRemoveTranslation,
  } = useLetterTemplateForm({
    active,
    isUpdate,
    handleClose,
    selectedTemplate,
  });

  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {/* Main Content - Form and Preview */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Form Section */}
        <Box
          sx={{
            flex: "1 1 100%",
            transition: "flex 0.3s ease",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", flex: 1 }}
          >
            {/* Template Information Section */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className="patient-form__info--header mt-1 mb-0">
                  Template Information
                </div>
                <p
                  style={{
                    color: "#757575",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  Enter the general information about this template
                </p>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormTextField
                  fullWidth
                  required
                  label="Template Name"
                  value={values.templateName}
                  onChange={(e) =>
                    setFieldValue("templateName", e.target.value)
                  }
                  submitted={submitClicked}
                  disabled={isSubmitting}
                  hasError={
                    (submitClicked && !values.templateName) ||
                    values.templateName?.length >= 75
                  }
                  errorMessage={
                    !values.templateName
                      ? "Template name is required"
                      : "Template name cannot exceed 75 characters"
                  }
                  inputProps={{ maxLength: 50 }}
                  sx={{
                    "& .MuiFormLabel-asterisk": { color: "red" },
                  }}
                />
              </Grid>

              {/* Accounts Multiselect */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal">
                  <Autocomplete
                    multiple
                    disableClearable
                    freeSolo={false}
                    disableCloseOnSelect
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      return filtered.length ? filtered : options;
                    }}
                    fullWidth
                    id="accounts"
                    name="accounts"
                    openOnFocus={true}
                    value={values.accounts || []}
                    renderTags={(value) =>
                      value?.map((account) => (
                        <Chip
                          key={account}
                          label={account}
                          size="small"
                          variant="outlined"
                          onDelete={() => {
                            setFieldValue(
                              "accounts",
                              value.filter((v) => v !== account)
                            );
                          }}
                        />
                      ))
                    }
                    options={accountOptions || []}
                    onChange={(e, value) => {
                      setFieldValue("accounts", value);
                    }}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;

                      return (
                        <li key={key} {...optionProps}>
                          <Checkbox checked={selected} />
                          {option}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Accounts"
                        variant="outlined"
                        type="text"
                        placeholder={
                          values.accounts?.length ? "" : "Search Accounts"
                        }
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal">
                  <Autocomplete
                    multiple
                    disableClearable
                    freeSolo={false}
                    disableCloseOnSelect
                    clearOnBlur
                    selectOnFocus
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      return filtered.length ? filtered : options;
                    }}
                    fullWidth
                    id="manufacturerArr"
                    name="manufacturerArr"
                    openOnFocus={true}
                    value={values.manufacturerArr || []}
                    renderTags={(value) =>
                      value?.map((manufacturer) => (
                        <Chip
                          key={manufacturer}
                          label={manufacturer}
                          size="small"
                          variant="outlined"
                          onDelete={() => {
                            setFieldValue(
                              "manufacturerArr",
                              value.filter((v) => v !== manufacturer)
                            );
                          }}
                        />
                      ))
                    }
                    options={manufacturerList || []}
                    onChange={(e, value) =>
                      setFieldValue("manufacturerArr", value)
                    }
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={key} {...optionProps}>
                          <Checkbox checked={selected} />
                          {option}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Manufacturers"
                        name="manufacturerArr"
                        variant="outlined"
                        type="text"
                        placeholder={
                          values.manufacturerArr?.length
                            ? ""
                            : "Search manufacturers"
                        }
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormSelectField
                  label="Associated Device Type"
                  value={values.associatedDevices || ""}
                  options={[
                    { label: "Select Device Type", value: "" },
                    ...LETTER_TEMPLATE_DEVICE_TYPES,
                  ]}
                  submitClicked={submitClicked}
                  onChange={(value) =>
                    setFieldValue("associatedDevices", value)
                  }
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal">
                  <Autocomplete
                    multiple
                    disableClearable
                    freeSolo={false}
                    disableCloseOnSelect
                    clearOnBlur
                    selectOnFocus
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      return filtered.length ? filtered : options;
                    }}
                    fullWidth
                    id="deviceModels"
                    name="deviceModels"
                    openOnFocus={true}
                    value={values.deviceModels || []}
                    renderTags={(value) =>
                      value?.map((model) => (
                        <Chip
                          key={model}
                          label={model}
                          size="small"
                          variant="outlined"
                          onDelete={() => {
                            setFieldValue(
                              "deviceModels",
                              value.filter((v) => v !== model)
                            );
                          }}
                        />
                      ))
                    }
                    options={deviceModelOptions || []}
                    onChange={(e, value) =>
                      setFieldValue("deviceModels", value)
                    }
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={key} {...optionProps}>
                          <Checkbox checked={selected} />
                          {option}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Device Models"
                        name="deviceModels"
                        variant="outlined"
                        type="text"
                        placeholder={
                          values.deviceModels?.length
                            ? ""
                            : "Search Device Models"
                        }
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container className="mt-2">
              {/* Show Battery Toggle */}
              <Grid item xs={3} md={2}>
                <FormControlLabel
                  control={
                    <Switch
                      name="showBattery"
                      checked={values.showBattery}
                      onChange={(e) =>
                        setFieldValue("showBattery", e.target.checked)
                      }
                      disabled={isSubmitting}
                    />
                  }
                  label="Show Battery"
                  labelPlacement="start"
                  style={{ marginLeft: 0, marginRight: "2px" }}
                  className="toggle-label"
                />
              </Grid>

              <Grid item xs={3} md={2}>
                <FormControlLabel
                  control={
                    <Switch
                      name="customNextSend"
                      checked={values.customNextSend}
                      onChange={(e) =>
                        setFieldValue("customNextSend", e.target.checked)
                      }
                      disabled={isSubmitting}
                    />
                  }
                  label="Custom Next Send"
                  labelPlacement="start"
                  style={{
                    marginLeft: 0,
                    marginRight: "2px",
                    whiteSpace: "nowrap",
                  }}
                  className="toggle-label"
                />
              </Grid>
            </Grid>

            <Divider className="mt-3 mb-2" />

            {/* Missing Transmissions Section */}
            <Grid container spacing={2}>
              <Grid item xs={6} md={6}>
                <div className="patient-form__info--header mt-1 mb-0">
                  Missing Transmissions
                </div>
                <p
                  style={{
                    color: "#757575",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  Configure settings for missing transmission notifications
                </p>
              </Grid>

              {/* Enable Transmission Toggle */}
              <Grid
                item
                xs={6}
                md={6}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="missingTransmissions.active"
                      checked={values.missingTransmissions.active}
                      onChange={(e) =>
                        setFieldValue(
                          "missingTransmissions.active",
                          e.target.checked
                        )
                      }
                      disabled={isSubmitting}
                    />
                  }
                  label="Enable transmission"
                  labelPlacement="start"
                  style={{ marginLeft: 0, marginRight: "2px" }}
                  className="toggle-label"
                />
              </Grid>

              {/* Transmission Cycle Select */}
              <Grid item xs={12} md={6}>
                <FormSelectField
                  label="Transmission Cycle"
                  value={values.missingTransmissions.transmissionCycle}
                  options={[
                    { label: "Select Cycle", value: "" },
                    ...TRANSMISSION_CYCLE_OPTIONS,
                  ]}
                  required={values.missingTransmissions.active}
                  submitClicked={submitClicked}
                  hasError={
                    submitClicked &&
                    values.missingTransmissions.active &&
                    !values.missingTransmissions.transmissionCycle
                  }
                  errorMessage="Transmission cycle is required"
                  onChange={(value) =>
                    setFieldValue(
                      "missingTransmissions.transmissionCycle",
                      value
                    )
                  }
                  disabled={isSubmitting || !values.missingTransmissions.active}
                />
              </Grid>

              {/* Use Saved Next Send Cycle Toggle */}
              <Grid
                item
                xs={12}
                md={3}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="missingTransmissions.useSavedNextSendCycle"
                      checked={
                        values.missingTransmissions.useSavedNextSendCycle
                      }
                      onChange={(e) =>
                        setFieldValue(
                          "missingTransmissions.useSavedNextSendCycle",
                          e.target.checked
                        )
                      }
                      disabled={
                        isSubmitting || !values.missingTransmissions.active
                      }
                    />
                  }
                  label="Use Saved Next Send Cycle"
                  labelPlacement="start"
                  style={{ marginLeft: 0, marginRight: "2px" }}
                  className="toggle-label"
                />
              </Grid>
            </Grid>

            <Divider className="mt-3 mb-2" />

            {/* Letter Template + Preview Section */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <div
                  className="d-flex justify-between align-items-center"
                  style={{ gap: "8px" }}
                >
                  <div>
                    <div className="patient-form__info--header mt-1 mb-0">
                      Letter Template
                    </div>
                    <p
                      style={{
                        color: "#757575",
                        fontSize: "12px",
                        lineHeight: "16px",
                      }}
                    >
                      Enter the content for the primary letter template
                    </p>
                  </div>
                  <FormControlLabel
                    control={
                      <Switch
                        name="htmlEditor"
                        checked={isHtmlEditor}
                        onChange={(e) => setIsHtmlEditor(e.target.checked)}
                        disabled={isSubmitting}
                      />
                    }
                    label="HTML Editor"
                    labelPlacement="start"
                    style={{ marginLeft: 0, marginRight: 0 }}
                    className="toggle-label"
                  />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div
                  className="d-flex justify-between align-items-center"
                  style={{ gap: "8px" }}
                >
                  <div>
                    <div className="patient-form__info--header mt-1 mb-0">
                      Preview Letter Template
                    </div>
                    <p
                      style={{
                        color: "#757575",
                        fontSize: "12px",
                        lineHeight: "16px",
                      }}
                    >
                      Preview the letter template with different languages
                    </p>
                  </div>
                  <Button
                    variant="outlined"
                    color="secondary"
                    label="Print Preview"
                    startIcon={<Print fontSize="small" />}
                    onClick={handlePrintPreview}
                    style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
                  />
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* Editor column: Translation hub + HTML textarea */}
              <Grid item xs={12} md={12}>
                <Grid container spacing={2}>
                  {/* Translation Hub */}
                  <Grid item xs={12} md={2} sm={2}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: "#757575",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      TRANSLATION HUB
                    </Typography>
                    <Box
                      sx={{
                        border: "0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <List disablePadding>
                        {hubItems.map((item) => (
                          <ListItemButton
                            key={item.id}
                            selected={selectedLanguageTab === item.id}
                            onClick={() => setSelectedLanguageTab(item.id)}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "#eaf4e6",
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor: "#e1efdb",
                              },
                            }}
                          >
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{
                                fontSize: "14px",
                                fontWeight:
                                  selectedLanguageTab === item.id ? 600 : 400,
                                color:
                                  selectedLanguageTab === item.id
                                    ? "#2e7d32"
                                    : "#333",
                              }}
                            />
                            {!item.isPrimary && (
                              <Tooltip title="Remove translation">
                                <IconButton
                                  size="small"
                                  edge="end"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTranslation(item.id);
                                  }}
                                  disabled={isSubmitting}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </ListItemButton>
                        ))}
                      </List>
                      <Box sx={{ p: 1 }}>
                        <Button
                          variant="text"
                          color="secondary"
                          label="+ Add Translation"
                          onClick={(e) => setAnchorEl(e.currentTarget)}
                          disabled={
                            isSubmitting || availableLanguages.length === 0
                          }
                          style={{ textTransform: "none" }}
                        />
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                        >
                          {availableLanguages.map((lang) => (
                            <MenuItem
                              key={lang.value}
                              onClick={() => {
                                handleAddTranslation(lang.value);
                                setAnchorEl(null);
                              }}
                            >
                              {lang.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    </Box>
                  </Grid>

                  {/* HTML Editor */}
                  <Grid item xs={12} md={5} sm={5} style={{ paddingTop: 0 }}>
                    <FormTextField
                      fullWidth
                      multiline
                      minRows={24}
                      maxRows={24}
                      placeholder="Enter HTML template content..."
                      value={getActiveContent()}
                      onChange={(e) => setActiveContent(e.target.value)}
                      submitted={submitClicked}
                      hasError={isActiveContentMissing}
                      errorMessage={
                        selectedLanguageTab === "primary"
                          ? "Primary template content is required"
                          : "Translation content is required"
                      }
                      disabled={isSubmitting || !isHtmlEditor}
                      InputProps={{
                        style: {
                          fontFamily: "monospace",
                          height: "100%",
                          minHeight: 488,
                          maxHeight: 488,
                          fontSize: "13px",
                        },
                      }}
                    />
                    <FormHelperText>
                      This field supports raw HTML content.
                    </FormHelperText>
                  </Grid>

                  {/* Preview column */}
                  <Grid item xs={12} md={5} sm={5}>
                    <LetterTemplatePreview content={getActiveContent()} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider className="mt-3 mb-3" />
          </form>
        </Box>
      </Box>

      {/* Footer Actions - Sticky */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          py: 2,
          px: 1,
          flexShrink: 0,
          backgroundColor: "#fff",
        }}
      >
        <Button
          variant="outlined"
          label="Cancel"
          onClick={handleCancel}
          disabled={isSubmitting}
        />
        <Button
          variant="contained"
          color="secondary"
          label={isUpdate ? "Update Template" : "Create Template"}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleSubmit}
        />
      </Box>
    </Box>
  );
};

export default LetterTemplateForm;
